import type { EnvironmentKey } from "common/constants/env";

/**
 * Defines a feature. Read the documentation here:
 * https://www.notion.so/higharc/Feature-Flags-c4a70e5576ec41659a87e7d760223f4b
 *
 * Overview of properties:
 *
 * 1. ownerName: The product manager or engineer primarily responsible for the development of the feature.
 * 2. description: A brief overview of the feature.
 * 3. status: Status of the feature (alpha, closed beta, open beta, etc.).
 * 4. hideWhenDisabled: If true, the feature will not be shown in the UI when disabled from environmentValues.
 * 5. settings:
 *   - defaultValue: Enable or disable the feature by default. Overridable.
 *   - environmentValues: Enable or disable the feature, on a per-environment basis. Non-overridable.
 */
export type FeatureStatus =
  | "experimental"
  | "alpha"
  | "closed_beta"
  | "open_beta"
  | "general_availability"
  | "deprecated";

export interface Feature {
  ownerName: string;
  description: string;
  status: FeatureStatus;
  hideWhenDisabled?: boolean;
  settings: {
    defaultValue: boolean;
    environmentValues?: {
      [key in EnvironmentKey]?: boolean;
    };
  };
}

export interface Features {
  [key: string]: Feature;
}

// Keep alphabetical for easier reading, except for the sample test flag on top
export const featureNames = [
  // test flag for automation
  "featureFlagForAutomatedTests",
  // real feature flags
  "agentAskMode",
  "agentBuildMode",
  "aiAdminTools",
  "aiDDEP",
  "ddepAutocomplete",
  "analyticEntityInspector",
  "autoTranslate",
  "cameraWalkthrough",
  "caseworkTags",
  "conditionalOpeningPlacement",
  "configTownhomes",
  "docRefInspectorV2",
  "docRefOptionLocks",
  "downloadHomePlanDataArchive",
  "optionCatalogElevationOverrides",
  "entityHostedAnnotations",
  "autoRenders",
  "fbxImport",
  "floorFraming",
  "geometry3DExport",
  "homePlanVersions",
  "horizontalAssemblies",
  "integrations",
  "workerQueues",
  "lots2",
  "manualScrollPlanOverviewControls",
  "nextGenOptions",
  "nextGenOptionsStudioOverrides",
  "nextGenOptionsEditor",
  "nextGenOptionsRemove",
  "nextGenOptionsMerge",
  "nextGenOptionsLocation",
  "legacyOptionCatalog",
  "patchBasedSave",
  "perEdgeEaveCutType",
  "wallFramingForEstimate",
  "floorFramingForEstimate",
  "procureKovaExport",
  "procureMappingsView",
  "procureResourceTag",
  "procureSettings",
  "propScaling",
  "qrCodes",
  "roofFraming",
  "roofSidesInspector",
  "roofVolumes",
  "roughOpeningInspector",
  "sharedWebGLInShowroom",
  "sharedWebGLInStudio",
  "showroomServerSideGLTF",
  "showroomTimingWidget",
  "showroomUnification",
  "showroomWorkerBuildingGen",
  "siteMode2",
  "studioGeneratedEntityIntegerAllocation",
  "textBoxDefinedSize",
  "modelComponents",
  "userDefinedPlansets",
  "procureResourceMappingConditions",
  "levelsPlacedByDatums",
  "modelHostedDrawingTemplates",
  "multiNoteFontSizeEditing",
  "optionCatalogHistory",
  "optionStateV2",
  "widgets",
  "spatialSearch",
  "newSpanStratumDatumInspector",
  "connectorMappings",
  "ephemeralEntityUsage",
  "selectedReportingUnitStudioPreview",
  "integrationAgent",
  "roofDormers",
  "estimateCompareBom",
  "estimationTasks",
  "sectionCallouts",
  "exteriorFinishEdgesForEstimate",
  "studioMultiEntityInspector",
  "wallPlanes",
  "compositeLevels",
  "locationWorkbooks",
] as const;

export type FeatureName = (typeof featureNames)[number];

/**
 * The key in each individual object serves as the name of the
 * feature flag as it is stored on the builder or user. For example,
 * if a builder's feature_flags object looks like this:
 *
 * { "hotspots": true }
 *
 * The hotspots key will map to a hotspots object within the
 * features object below.
 *
 * Order of priority when determining if a feature is enabled
 * in a given feature and env (highest to lowest):
 *
 * 1. settings.environmentValues[env] - once set, the feature is turned on for all users on a given env
 * 2. builder.featureFlags[feature] - if in the context of a builder
 * 3. builderUser.featureFlags[feature] - if in the context of a builder the user belongs to
 * 4. user.featureFlags[feature] - setting for individual user, regardless of builder
 * 5. settings.defaultValue - the fallback value if no others are set
 */
export const features: { [key in FeatureName]: Feature } = {
  featureFlagForAutomatedTests: {
    ownerName: "N/A",
    description: "This is only used for testing purposes",
    status: "experimental",
    settings: { defaultValue: true },
  },
  downloadHomePlanDataArchive: {
    ownerName: "Lara Kelley",
    description: "Download all Home Plan data for a builder.",
    status: "alpha",
    settings: {
      defaultValue: false,
      environmentValues: {
        prod: false,
      },
    },
  },
  manualScrollPlanOverviewControls: {
    ownerName: "Tucker Blackwell",
    description: "Display buttons in Showroom to scroll the options list on large touch-screen displays (i.e. TVs)",
    status: "general_availability",
    settings: {
      defaultValue: false,
    },
  },
  legacyOptionCatalog: {
    ownerName: "Matt Spencer",
    description:
      "Manage a builder's Option Catalog from Studio or the Admin Dashboard. Options are persisted to the DB.",
    status: "deprecated",
    settings: {
      defaultValue: false,
    },
  },
  procureResourceMappingConditions: {
    ownerName: "Thad Woodman",
    description: "Enable option conditions for resource mappings in Procure 2.0",
    status: "closed_beta",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
        develop: true,
      },
    },
  },
  wallFramingForEstimate: {
    ownerName: "Andrew Salveson",
    description: "Wall Framing For Estimate",
    status: "alpha",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
        develop: true,
      },
    },
  },
  floorFramingForEstimate: {
    ownerName: "Rakesh Pathak",
    description: "Floor Framing For Estimate",
    status: "alpha",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
        develop: true,
      },
    },
  },
  procureResourceTag: {
    ownerName: "Nick Sinclair",
    description: "Adds ability to add custom resource tags in integrations",
    status: "closed_beta",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
      },
    },
  },
  procureSettings: {
    ownerName: "Nick Sinclair",
    description: "Adds ability to configure settings in Procure",
    status: "open_beta",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
      },
    },
  },
  siteMode2: {
    ownerName: "Michael Bergin",
    description: "Enables dynamic modeling of sites in Studio",
    status: "open_beta",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
        develop: true,
      },
    },
  },
  agentAskMode: {
    ownerName: "Michael Bergin",
    description: "Agent Ask Mode (Help Assistant) for Studio",
    status: "closed_beta",
    settings: {
      defaultValue: false,
      environmentValues: {
        sandbox1: true,
      },
    },
  },
  agentBuildMode: {
    ownerName: "Michael Bergin",
    description: "Agent Build Mode (Drafter Assistant) for Studio. Experimental, use with caution.",
    status: "alpha",
    settings: {
      defaultValue: false,
      environmentValues: {},
    },
  },
  roofSidesInspector: {
    ownerName: "Tucker Blackwell/Mauro Lopez",
    description: "Display Roof Sides Inspector in Studio",
    status: "general_availability",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
        develop: true,
      },
    },
  },
  horizontalAssemblies: {
    ownerName: "Rakesh Pathak/Luis Jaggy/Scot Wilcox",
    description:
      "Horizontal Assemblies (Dependent on docRef fixes for foreignKeyId reverse references, talk to @nathan for more details)",
    status: "alpha",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
        develop: true,
      },
    },
  },
  floorFraming: {
    ownerName: "Rakesh Pathak",
    description: "Floor Framing",
    status: "alpha",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
        develop: true,
      },
    },
  },
  roofFraming: {
    ownerName: "Rakesh Pathak/Mauro Lopez",
    description: "Roof Framing",
    status: "experimental",
    settings: {
      defaultValue: false,
      environmentValues: {
        stage: false,
        prod: false,
      },
    },
  },
  roughOpeningInspector: {
    ownerName: "Nathan Lowe",
    description: "Display Rough Opening Inspector in Studio",
    status: "experimental",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: false,
        develop: false,
        stage: false,
        prod: false,
      },
    },
  },
  userDefinedPlansets: {
    ownerName: "Peter Boyer",
    description: "Define your own plan set templates",
    status: "open_beta",
    settings: {
      defaultValue: true,
      environmentValues: {
        local: true,
        sandbox2: true,
        develop: true,
      },
    },
  },
  autoTranslate: {
    ownerName: "Ardavan Bidgoli",
    description: "AI tools for Auto-Translate, Internal Use",
    status: "closed_beta",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
        develop: true,
      },
    },
  },
  aiAdminTools: {
    ownerName: "Ardavan Bidgoli",
    description: "Experimental AI features in Development",
    status: "experimental",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
        develop: true,
        stage: false,
        prod: false,
      },
    },
  },
  entityHostedAnnotations: {
    ownerName: "Mauro Lopez",
    description: "Entity hosted annotations",
    status: "closed_beta",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
      },
    },
  },
  qrCodes: {
    ownerName: "Michael Bergin",
    description: "Add QR Codes to Built-in Plansets linked to Showroom",
    status: "closed_beta",
    settings: {
      defaultValue: false,
      environmentValues: {},
    },
  },
  roofVolumes: {
    ownerName: "Mauro Lopez",
    description: "Roof Volumes",
    status: "experimental",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: false,
      },
    },
  },
  aiDDEP: {
    ownerName: "Manuel Rodriguez",
    description: "Features for Data Driven Entity Prediction (DDEP)",
    status: "closed_beta",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
        develop: true,
      },
    },
  },
  ddepAutocomplete: {
    ownerName: "Manuel Rodriguez",
    description:
      "TAB-based autocomplete for DDEP. Silently predicts furniture when rooms are selected and allows acceptance via TAB.",
    status: "experimental",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
        develop: true,
      },
    },
  },
  perEdgeEaveCutType: {
    ownerName: "Mauro Lopez",
    description: "Allow eave cut type to be set per roof side/edge",
    status: "closed_beta",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
      },
    },
  },
  integrations: {
    ownerName: "Peter Maffey",
    description: "Configure ERP integrations",
    status: "open_beta",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
        develop: true,
      },
    },
  },
  procureKovaExport: {
    ownerName: "Misael Leyton",
    description: "Show/hide the KOVA export button in Procure",
    status: "closed_beta",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
        develop: true,
      },
    },
  },
  procureMappingsView: {
    ownerName: "Cory Ortega",
    description: "Show/hide the Mappings view in Estimate",
    status: "open_beta",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
        develop: true,
      },
    },
  },
  configTownhomes: {
    ownerName: "Jesse Kocher",
    description: "Enable townhome buildings in Config and Showroom",
    status: "alpha",
    settings: {
      defaultValue: false,
    },
  },
  patchBasedSave: {
    ownerName: "Mike Starr",
    description: "Use JSON Patch to save Studio Documents",
    status: "experimental",
    settings: {
      defaultValue: false,
      environmentValues: {
        prod: false,
      },
    },
  },
  studioGeneratedEntityIntegerAllocation: {
    ownerName: "Andrew Salveson",
    description: "Enable allocation of integers for generated entity handles in Studio",
    status: "experimental",
    settings: {
      defaultValue: false,
    },
  },
  cameraWalkthrough: {
    ownerName: "Michael Bergin",
    description: "Interior Cameras",
    status: "alpha",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
        develop: true,
        sandbox1: true,
      },
    },
  },
  fbxImport: {
    ownerName: "Paul Merrell",
    description: "Import FBX files into Studio",
    status: "open_beta",
    hideWhenDisabled: true,
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
        develop: true,
      },
    },
  },
  propScaling: {
    ownerName: "Paul Merrell",
    description: "Show option of scaling the props",
    status: "experimental",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
        develop: true,
      },
    },
  },
  modelComponents: {
    ownerName: "Paul Merrell",
    description: "Allow rooms and other entities to be grouped into model components.",
    status: "alpha",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
      },
    },
  },
  docRefOptionLocks: {
    ownerName: "Peter Maffey",
    description: "Doc ref option locking conditions",
    status: "open_beta",
    settings: {
      defaultValue: false,
    },
  },
  analyticEntityInspector: {
    ownerName: "Andrew Salveson",
    description: "Show Analytic Entity Inspector in Studio",
    status: "experimental",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
        develop: true,
      },
    },
  },
  optionCatalogElevationOverrides: {
    ownerName: "Matt Spencer",
    description: "Elevation-specific overrides in the option catalog",
    status: "alpha",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
        develop: true,
      },
    },
  },
  lots2: {
    ownerName: "Lara Kelley",
    description: "Redesigned Lots table",
    status: "alpha",
    settings: {
      defaultValue: false,
      environmentValues: {
        prod: false,
      },
    },
  },
  nextGenOptions: {
    ownerName: "Peter Maffey",
    description: "Refactor options into builder and studio catalogs",
    status: "open_beta",
    hideWhenDisabled: true,
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
        develop: true,
      },
    },
  },
  nextGenOptionsStudioOverrides: {
    ownerName: "Edith Chow",
    description: "Force studio overrides into catalog",
    status: "experimental",
    hideWhenDisabled: true,
    settings: {
      defaultValue: false,
    },
  },
  nextGenOptionsEditor: {
    ownerName: "Peter Maffey",
    description: "Edit options in the Option Catalog",
    status: "open_beta",
    hideWhenDisabled: true,
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
        develop: true,
      },
    },
  },
  nextGenOptionsRemove: {
    ownerName: "Edith Chow",
    description: "Allow master plan option usages to be removed",
    status: "experimental",
    hideWhenDisabled: true,
    settings: {
      defaultValue: false,
    },
  },
  nextGenOptionsMerge: {
    ownerName: "Edith Chow",
    description: "Merge option sets in the Option Catalog",
    status: "experimental",
    hideWhenDisabled: true,
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
        develop: true,
      },
    },
  },
  nextGenOptionsLocation: {
    ownerName: "Edith Chow",
    description: "Enables Options tab in Locations",
    status: "experimental",
    hideWhenDisabled: true,
    settings: {
      defaultValue: false,
    },
  },
  docRefInspectorV2: {
    ownerName: "Nathan Lowe",
    description:
      "Enable doc preview edit mode (batch changes that are previewed prior to being applied). Specifically for doc ref changes.",
    hideWhenDisabled: true,
    status: "experimental",
    settings: {
      defaultValue: false,
    },
  },
  textBoxDefinedSize: {
    ownerName: "Jamie Madill",
    description: "Create text boxes of a defined size.",
    status: "closed_beta",
    settings: {
      defaultValue: false,
    },
  },
  sharedWebGLInStudio: {
    ownerName: "Olli Etuaho",
    description: "Enable shared WebGL context for Studio",
    status: "alpha",
    settings: {
      defaultValue: false,
    },
  },
  caseworkTags: {
    ownerName: "Jamie Madill",
    description: "Enable casework tags in studio",
    status: "closed_beta",
    settings: {
      defaultValue: false,
    },
  },
  sharedWebGLInShowroom: {
    ownerName: "Olli Etuaho",
    description:
      "Enable shared WebGL context for Showroom. Note that this can be controlled only at the server level, not on a per-user basis.",
    status: "closed_beta",
    settings: {
      defaultValue: false,
    },
  },
  showroomServerSideGLTF: {
    ownerName: "Jamie Madill",
    description: "Generate showroom GLTF on server instead of client-side building generation",
    status: "experimental",
    settings: {
      defaultValue: false,
    },
  },
  showroomTimingWidget: {
    ownerName: "Jamie Madill",
    description: "Display timing widget in Showroom for performance monitoring.",
    status: "general_availability",
    settings: {
      defaultValue: false,
    },
  },
  showroomUnification: {
    ownerName: "Peter Maffey",
    description: "Deprecate legacy Showroom for new showroom2 UI.",
    status: "experimental",
    hideWhenDisabled: true,
    settings: {
      defaultValue: false,
      environmentValues: { prod: false },
    },
  },
  showroomWorkerBuildingGen: {
    ownerName: "Jamie Madill",
    description: "Move Showroom building generation and rendering to a worker thread.",
    status: "experimental",
    settings: {
      defaultValue: false,
    },
  },
  conditionalOpeningPlacement: {
    ownerName: "Nathan Lowe",
    description: "Window and door placement can be set to multiple positions conditionally",
    status: "open_beta",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
        develop: true,
      },
    },
  },
  geometry3DExport: {
    ownerName: "Olli Etuaho",
    description: "Enable 3D geometry export in studio (glTF). Should only be enabled for Higharc internal use",
    status: "experimental",
    settings: {
      defaultValue: false,
    },
  },
  homePlanVersions: {
    ownerName: "Jim Hoskins",
    description: "Freeze downstream option data at a point in time for home plans",
    status: "experimental",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
        develop: true,
      },
    },
  },
  levelsPlacedByDatums: {
    ownerName: "Mauro Lopez",
    description:
      "Enable levels placed by datums in studio. Should only be enabled for Higharc internal use. Requires origin relative datums.",
    status: "experimental",
    hideWhenDisabled: true,
    settings: {
      defaultValue: false,
    },
  },
  modelHostedDrawingTemplates: {
    ownerName: "Olli Etuaho",
    description: "Ability to host drawing templates to rooms, levels, elevation views, and section views",
    status: "closed_beta",
    settings: {
      defaultValue: false,
    },
  },
  widgets: {
    ownerName: "Andrew Salveson",
    description: "Demo 3D widgets in Studio",
    status: "experimental",
    settings: {
      defaultValue: false,
    },
  },
  spatialSearch: {
    ownerName: "Andrew Salveson",
    description: "Spatial search in Studio",
    status: "experimental",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
        develop: true,
      },
    },
  },
  multiNoteFontSizeEditing: {
    ownerName: "Paul Scrugham",
    description: "Enable multi-note font size editing in studio",
    status: "alpha",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
        develop: true,
      },
    },
  },
  optionCatalogHistory: {
    ownerName: "Matt Spencer",
    description: "Option catalog change history tab on master plans",
    status: "alpha",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
        develop: true,
      },
    },
  },
  optionStateV2: {
    ownerName: "Thad Woodman",
    description: "Centralize option choice preprocessing (rules, locks, sanitization) before building generation",
    status: "alpha",
    settings: {
      defaultValue: false,
      environmentValues: {},
    },
  },
  newSpanStratumDatumInspector: {
    ownerName: "Mauro Lopez",
    description: "Enable new datum usage UX for facade span strata",
    status: "alpha",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
      },
    },
  },
  integrationAgent: {
    ownerName: "Bilal Choudhry",
    description: "Agent for ERP integrations",
    status: "experimental",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
      },
    },
  },
  connectorMappings: {
    ownerName: "Bilal Choudhry",
    description: "Interface for managing connector mappings",
    status: "alpha",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
        develop: true,
      },
    },
  },
  ephemeralEntityUsage: {
    ownerName: "Mauro Lopez",
    description: "Enable ephemeral entity usage in studio",
    status: "experimental",
    hideWhenDisabled: true,
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
        develop: true,
      },
    },
  },
  selectedReportingUnitStudioPreview: {
    ownerName: "Olli Etuaho",
    description: "Enable Studio preview of selected reporting unit",
    status: "experimental",
    settings: {
      defaultValue: false,
      environmentValues: {},
    },
  },
  workerQueues: {
    ownerName: "Jared Robertson",
    description: "Route integrations import jobs through bullmq to be executed in the worker",
    status: "experimental",
    hideWhenDisabled: true,
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
        develop: true,
        sandbox2: true,
      },
    },
  },
  autoRenders: {
    ownerName: "Andrew Salveson",
    description: "Auto Renders",
    status: "closed_beta",
    hideWhenDisabled: true,
    settings: {
      defaultValue: false,
      environmentValues: {},
    },
  },
  roofDormers: {
    ownerName: "Tucker Blackwell",
    description: "Use of (deprecated) roof dormers in Studio",
    status: "deprecated",
    settings: {
      defaultValue: false,
    },
  },
  estimateCompareBom: {
    ownerName: "Misael Leyton",
    description: "Enable BOM version comparison in Procure",
    status: "experimental",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
        develop: true,
      },
    },
  },
  estimationTasks: {
    ownerName: "Nick Sinclair",
    description: "Estimation tasks for vendors",
    status: "experimental",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
        develop: true,
        sandbox15: true,
      },
    },
  },
  sectionCallouts: {
    ownerName: "Henry Allen",
    description: "Renders section callouts in plans",
    status: "experimental",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
        develop: true,
      },
    },
  },
  exteriorFinishEdgesForEstimate: {
    ownerName: "Paul Scrugham",
    description: "Enable exterior finish edges for estimate",
    status: "experimental",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
        develop: true,
      },
    },
  },
  studioMultiEntityInspector: {
    ownerName: "Tucker Blackwell",
    description: "Enable multi-inspector in studio",
    status: "experimental",
    settings: {
      defaultValue: false,
    },
  },
  wallPlanes: {
    ownerName: "Scot Wilcox",
    description: "Enable BuildingGraph generation and related wall planes pipeline",
    status: "experimental",
    settings: {
      defaultValue: false,
    },
  },
  compositeLevels: {
    ownerName: "Henry Allen",
    description: "Enable placing multiple levels in live plans",
    status: "experimental",
    hideWhenDisabled: true,
    settings: {
      defaultValue: false,
    },
  },
  locationWorkbooks: {
    ownerName: "Jay Zhao",
    description: "Associate a location with a workbook",
    status: "experimental",
    settings: {
      defaultValue: false,
      environmentValues: {
        local: true,
        develop: true,
      },
    },
  },
} as const;

export type FeatureSet = { [key in FeatureName]?: boolean };
